import { useState } from 'react';

export default function Corporate() {
  const [formData, setFormData] = useState({
    companyName: '',
    name: '',
    email: '',
    phone: '',
    employeeCount: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d35vbcdhl5d9vdijb9tg', {
        method: 'POST',
        body: new URLSearchParams(formDataToSend as any)
      });

      if (response.ok) {
        setSubmitStatus('資料請求を受け付けました。後日担当者よりご連絡いたします。');
        setFormData({
          companyName: '',
          name: '',
          email: '',
          phone: '',
          employeeCount: '',
          message: ''
        });
      } else {
        setSubmitStatus('送信に失敗しました。お手数ですが再度お試しください。');
      }
    } catch (error) {
      setSubmitStatus('送信に失敗しました。お手数ですが再度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-orange-50 px-4 sm:px-6">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/ad0e5ad2054edeaed90cce6e171f8a0a.jpg')`
          }}
        />
        <div className="relative z-10 max-w-6xl mx-auto text-center">
          <div className="mb-6 sm:mb-8">
            <img 
              src="https://static.readdy.ai/image/6d1552f340f8b16d5538e9b5dc05368f/307cb145ec2a1832180b8031a904da90.png"
              alt="Salad Fight Logo"
              className="h-16 sm:h-20 md:h-24 mx-auto"
            />
          </div>
          
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-800 mb-4 sm:mb-6 leading-tight px-4">
            社員の健康が、<br />
            会社のパフォーマンスを変える
          </h1>
          
          <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-8 sm:mb-12 max-w-4xl mx-auto leading-relaxed px-4">
            福利厚生で"社食難民"を解決。採用と定着に効く健康ランチを、<br className="hidden sm:block" />
            まずは資料でご確認ください。
          </p>
          
          <div className="flex flex-col gap-4 sm:gap-6 justify-center items-center px-4">
            <button 
              onClick={() => document.getElementById('download-form')?.scrollIntoView({ behavior: 'smooth' })}
              className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-bold text-base sm:text-lg whitespace-nowrap transition-colors cursor-pointer shadow-lg"
            >
              <i className="ri-download-line mr-2"></i>
              法人向け資料をダウンロードする
            </button>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-800 mb-12 sm:mb-16">
            こんなお悩みありませんか？
          </h2>
          
          <div className="grid gap-6 sm:gap-8 md:grid-cols-3">
            <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-lg text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <i className="ri-restaurant-line text-2xl text-red-500"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4">社食がなく昼休みは行列</h3>
              <p className="text-sm sm:text-base text-gray-600">
                近隣の飲食店は混雑で待ち時間が長く、社員が疲弊している状況が続いています。
              </p>
            </div>
            
            <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-lg text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <i className="ri-heart-pulse-line text-2xl text-yellow-500"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4">健康経営が形だけ</h3>
              <p className="text-sm sm:text-base text-gray-600">
                健康経営を掲げても実効性のある取り組みが少なく、社員の健康意識向上に課題があります。
              </p>
            </div>
            
            <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-lg text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <i className="ri-team-line text-2xl text-blue-500"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4">採用・定着の武器不足</h3>
              <p className="text-sm sm:text-base text-gray-600">
                競合他社との差別化が難しく、魅力的な福利厚生で人材確保・定着を図りたい。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-800 mb-12 sm:mb-16">
            私たちの解決アプローチ
          </h2>
          
          <div className="grid gap-8 md:grid-cols-3 mb-12 sm:mb-16">
            <div className="text-center">
              <div className="w-20 sm:w-24 h-20 sm:h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <i className="ri-ticket-line text-2xl sm:text-3xl text-green-600"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4">紙チケット制で導入簡単</h3>
              <p className="text-sm sm:text-base text-gray-600">
                3ヶ月または半年契約で手軽にスタート。複雑なシステム導入は不要です。
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 sm:w-24 h-20 sm:h-24 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <i className="ri-map-pin-line text-2xl sm:text-3xl text-orange-600"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4">肥後橋直結でアクセス良好</h3>
              <p className="text-sm sm:text-base text-gray-600">
                大阪・肥後橋直結の好立地。社員の移動時間を最小限に抑えます。
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 sm:w-24 h-20 sm:h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <i className="ri-leaf-line text-2xl sm:text-3xl text-green-600"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4">健康的サラダで集中力維持</h3>
              <p className="text-sm sm:text-base text-gray-600">
                栄養バランスの取れたサラダで、午後の集中力・記憶力をサポートします。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Menu Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-800 mb-4">
            豊富なメニュー
          </h2>
          <p className="text-center text-gray-600 mb-12 sm:mb-16 text-base sm:text-lg">
            サラダもアサイーも選べる
          </p>
          
          <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
            <div className="flex gap-4 pb-4">
              <div className="flex-shrink-0 w-48 bg-white rounded-xl shadow-md overflow-hidden border border-gray-100" data-product-shop>
                <div className="aspect-square overflow-hidden bg-gray-50">
                  <img alt="豚しゃぶ先生" className="w-full h-full object-cover" src="https://saladfight.jp/cdn/shop/files/093496906eb8fc892be0b6ee6f94aa23_a1b09eb8-5fa0-43b2-a398-3734a5be7b68.png?v=1763719553&width=533" />
                </div>
                <div className="p-3">
                  <div className="text-xs text-green-600 font-medium mb-1">メインボウル</div>
                  <h3 className="font-bold text-gray-800 text-sm mb-1 line-clamp-2">豚しゃぶ先生</h3>
                  <p className="text-orange-600 font-bold text-sm">¥1,320から</p>
                </div>
              </div>
              
              <div className="flex-shrink-0 w-48 bg-white rounded-xl shadow-md overflow-hidden border border-gray-100" data-product-shop>
                <div className="aspect-square overflow-hidden bg-gray-50">
                  <img alt="シーザーサラダちゃん" className="w-full h-full object-cover" src="https://saladfight.jp/cdn/shop/files/7218d055f8273f5c34ebb92c4c2aebd6.png?v=1763721057&width=533" />
                </div>
                <div className="p-3">
                  <div className="text-xs text-green-600 font-medium mb-1">メインボウル</div>
                  <h3 className="font-bold text-gray-800 text-sm mb-1 line-clamp-2">シーザーサラダちゃん</h3>
                  <p className="text-orange-600 font-bold text-sm">¥1,380から</p>
                </div>
              </div>
              
              <div className="flex-shrink-0 w-48 bg-white rounded-xl shadow-md overflow-hidden border border-gray-100" data-product-shop>
                <div className="aspect-square overflow-hidden bg-gray-50">
                  <img alt="PFCトリオ" className="w-full h-full object-cover" src="https://saladfight.jp/cdn/shop/files/PFC_e8ad4f19-34c9-4d3a-85b2-c1271f5bb298.png?v=1763721112&width=533" />
                </div>
                <div className="p-3">
                  <div className="text-xs text-green-600 font-medium mb-1">メインボウル</div>
                  <h3 className="font-bold text-gray-800 text-sm mb-1 line-clamp-2">PFCトリオ</h3>
                  <p className="text-orange-600 font-bold text-sm">¥1,280から</p>
                </div>
              </div>
              
              <div className="flex-shrink-0 w-48 bg-white rounded-xl shadow-md overflow-hidden border border-gray-100" data-product-shop>
                <div className="aspect-square overflow-hidden bg-gray-50">
                  <img alt="明太子パスラ" className="w-full h-full object-cover" src="https://static.readdy.ai/image/6d1552f340f8b16d5538e9b5dc05368f/bd9043d464165784d0174304004d7655.png" />
                </div>
                <div className="p-3">
                  <div className="text-xs text-green-600 font-medium mb-1">メインボウル</div>
                  <h3 className="font-bold text-gray-800 text-sm mb-1 line-clamp-2">明太子パスラ</h3>
                  <p className="text-orange-600 font-bold text-sm">¥1,280から</p>
                </div>
              </div>
              
              <div className="flex-shrink-0 w-48 bg-white rounded-xl shadow-md overflow-hidden border border-gray-100" data-product-shop>
                <div className="aspect-square overflow-hidden bg-gray-50">
                  <img alt="アサイー王女" className="w-full h-full object-cover" src="https://saladfight.jp/cdn/shop/files/f9e00c0e24d33d38d731e2f72abae4f6.jpg?v=1748965475&width=533" />
                </div>
                <div className="p-3">
                  <div className="text-xs text-green-600 font-medium mb-1">メインボウル</div>
                  <h3 className="font-bold text-gray-800 text-sm mb-1 line-clamp-2">アサイー王女</h3>
                  <p className="text-orange-600 font-bold text-sm">¥1,380</p>
                </div>
              </div>
              
              <div className="flex-shrink-0 w-48 bg-white rounded-xl shadow-md overflow-hidden border border-gray-100" data-product-shop>
                <div className="aspect-square overflow-hidden bg-gray-50">
                  <img alt="ファイトサラダ" className="w-full h-full object-cover" src="https://saladfight.jp/cdn/shop/files/00e5126f852b066fbb0cc868801dc347_283a47b7-a911-4dc8-a956-a1168000bcac.png?v=1763719598&width=533" />
                </div>
                <div className="p-3">
                  <div className="text-xs text-green-600 font-medium mb-1">メインボウル</div>
                  <h3 className="font-bold text-gray-800 text-sm mb-1 line-clamp-2">ファイトサラダ</h3>
                  <p className="text-orange-600 font-bold text-sm">¥1,180から</p>
                </div>
              </div>
              
              <div className="flex-shrink-0 w-48 bg-white rounded-xl shadow-md overflow-hidden border border-gray-100" data-product-shop>
                <div className="aspect-square overflow-hidden bg-gray-50">
                  <img alt="マーボーくん" className="w-full h-full object-cover" src="https://saladfight.jp/cdn/shop/files/8ed45410def4761be07e70aaa30d5dda_4d383549-f2e7-4fb3-868d-05f282423e66.png?v=1763719490&width=533" />
                </div>
                <div className="p-3">
                  <div className="text-xs text-green-600 font-medium mb-1">メインボウル</div>
                  <h3 className="font-bold text-gray-800 text-sm mb-1 line-clamp-2">マーボーくん</h3>
                  <p className="text-orange-600 font-bold text-sm">¥1,280から</p>
                </div>
              </div>
              
              <div className="flex-shrink-0 w-48 bg-white rounded-xl shadow-md overflow-hidden border border-gray-100" data-product-shop>
                <div className="aspect-square overflow-hidden bg-gray-50">
                  <img alt="ローストビーフ兄弟" className="w-full h-full object-cover" src="https://saladfight.jp/cdn/shop/files/6abbc7578f89d9ef44fb66a43bc396b6_90e9704c-9b01-4b5f-9933-11418233b6d6.png?v=1763719391&width=533" />
                </div>
                <div className="p-3">
                  <div className="text-xs text-green-600 font-medium mb-1">メインボウル</div>
                  <h3 className="font-bold text-gray-800 text-sm mb-1 line-clamp-2">ローストビーフ兄弟</h3>
                  <p className="text-orange-600 font-bold text-sm">¥1,980から</p>
                </div>
              </div>
              
              <div className="flex-shrink-0 w-48 bg-white rounded-xl shadow-md overflow-hidden border border-gray-100" data-product-shop>
                <div className="aspect-square overflow-hidden bg-gray-50">
                  <img alt="【冬限定】マーラータン" className="w-full h-full object-cover" src="https://saladfight.jp/cdn/shop/files/1e29debd09781e312c93c6e2c97d17fa.png?v=1763719289&width=533" />
                </div>
                <div className="p-3">
                  <div className="text-xs text-green-600 font-medium mb-1">メインボウル</div>
                  <h3 className="font-bold text-gray-800 text-sm mb-1 line-clamp-2">【冬限定】マーラータン</h3>
                  <p className="text-orange-600 font-bold text-sm">¥1,480</p>
                </div>
              </div>
              
              <div className="flex-shrink-0 w-48 bg-white rounded-xl shadow-md overflow-hidden border border-gray-100" data-product-shop>
                <div className="aspect-square overflow-hidden bg-gray-50">
                  <img alt="ポキ王子" className="w-full h-full object-cover" src="https://saladfight.jp/cdn/shop/files/f91df01a72b0e4b43b8936c743e3ca90_cc99fb75-2f35-40c2-ac68-467b581bf156.png?v=1763719520&width=533" />
                </div>
                <div className="p-3">
                  <div className="text-xs text-green-600 font-medium mb-1">メインボウル</div>
                  <h3 className="font-bold text-gray-800 text-sm mb-1 line-clamp-2">ポキ王子</h3>
                  <p className="text-orange-600 font-bold text-sm">¥1,980から</p>
                </div>
              </div>
              
              <div className="flex-shrink-0 w-48 bg-white rounded-xl shadow-md overflow-hidden border border-gray-100" data-product-shop>
                <div className="aspect-square overflow-hidden bg-gray-50">
                  <img alt="ファイトローラー" className="w-full h-full object-cover" src="https://saladfight.jp/cdn/shop/files/b3decf8614c612a02fce8601e84bfa36.png?v=1748965109&width=533" />
                </div>
                <div className="p-3">
                  <div className="text-xs text-green-600 font-medium mb-1">サイドメニュー</div>
                  <h3 className="font-bold text-gray-800 text-sm mb-1 line-clamp-2">ファイトローラー</h3>
                  <p className="text-orange-600 font-bold text-sm">¥650</p>
                </div>
              </div>
              
              <div className="flex-shrink-0 w-48 bg-white rounded-xl shadow-md overflow-hidden border border-gray-100" data-product-shop>
                <div className="aspect-square overflow-hidden bg-gray-50">
                  <img alt="とりむねブロッコリー" className="w-full h-full object-cover" src="https://saladfight.jp/cdn/shop/files/0cd8ea62a8290cecc04982a74eeb5347_13535b7f-2f6d-4780-b8d7-299193d71ea1.jpg?v=1763719363&width=533" />
                </div>
                <div className="p-3">
                  <div className="text-xs text-green-600 font-medium mb-1">サイドメニュー</div>
                  <h3 className="font-bold text-gray-800 text-sm mb-1 line-clamp-2">とりむねブロッコリー</h3>
                  <p className="text-orange-600 font-bold text-sm">¥650</p>
                </div>
              </div>
              
              <div className="flex-shrink-0 w-48 bg-white rounded-xl shadow-md overflow-hidden border border-gray-100" data-product-shop>
                <div className="aspect-square overflow-hidden bg-gray-50">
                  <img alt="ファイトカップ" className="w-full h-full object-cover" src="https://saladfight.jp/cdn/shop/files/3bd93ff1b4f41e6f35afa01e8dad52b2.jpg?v=1748965126&width=533" />
                </div>
                <div className="p-3">
                  <div className="text-xs text-green-600 font-medium mb-1">サイドメニュー</div>
                  <h3 className="font-bold text-gray-800 text-sm mb-1 line-clamp-2">ファイトカップ</h3>
                  <p className="text-orange-600 font-bold text-sm">¥650</p>
                </div>
              </div>
              
              <div className="flex-shrink-0 w-48 bg-white rounded-xl shadow-md overflow-hidden border border-gray-100" data-product-shop>
                <div className="aspect-square overflow-hidden bg-gray-50">
                  <img alt="豚しゃぶカップ" className="w-full h-full object-cover" src="https://saladfight.jp/cdn/shop/files/e2ab08c29d6f35480a254ad1a709a9af.jpg?v=1748965092&width=533" />
                </div>
                <div className="p-3">
                  <div className="text-xs text-green-600 font-medium mb-1">サイドメニュー</div>
                  <h3 className="font-bold text-gray-800 text-sm mb-1 line-clamp-2">豚しゃぶカップ</h3>
                  <p className="text-orange-600 font-bold text-sm">¥650</p>
                </div>
              </div>
              
              <div className="flex-shrink-0 w-48 bg-white rounded-xl shadow-md overflow-hidden border border-gray-100" data-product-shop>
                <div className="aspect-square overflow-hidden bg-gray-50">
                  <img alt="アサイースムージー" className="w-full h-full object-cover" src="https://saladfight.jp/cdn/shop/files/afa7b2f7227a4ad00e985826f1a5ffa3.jpg?v=1748965213&width=533" />
                </div>
                <div className="p-3">
                  <div className="text-xs text-green-600 font-medium mb-1">サイドメニュー</div>
                  <h3 className="font-bold text-gray-800 text-sm mb-1 line-clamp-2">アサイースムージー</h3>
                  <p className="text-orange-600 font-bold text-sm">¥650</p>
                </div>
              </div>
              
              <div className="flex-shrink-0 w-48 bg-white rounded-xl shadow-md overflow-hidden border border-gray-100" data-product-shop>
                <div className="aspect-square overflow-hidden bg-gray-50">
                  <img alt="ソイバナナスムージー" className="w-full h-full object-cover" src="https://saladfight.jp/cdn/shop/files/047bb44ad1ec4e24004d677199da9e71.jpg?v=1748965325&width=533" />
                </div>
                <div className="p-3">
                  <div className="text-xs text-green-600 font-medium mb-1">サイドメニュー</div>
                  <h3 className="font-bold text-gray-800 text-sm mb-1 line-clamp-2">ソイバナナスムージー</h3>
                  <p className="text-orange-600 font-bold text-sm">¥650</p>
                </div>
              </div>
              
              <div className="flex-shrink-0 w-48 bg-white rounded-xl shadow-md overflow-hidden border border-gray-100" data-product-shop>
                <div className="aspect-square overflow-hidden bg-gray-50">
                  <img alt="ホットコーヒー" className="w-full h-full object-cover" src="https://saladfight.jp/cdn/shop/files/3b18e7e179b80224461d6fab7cda5f4b.png?v=1762405426&width=533" />
                </div>
                <div className="p-3">
                  <div className="text-xs text-green-600 font-medium mb-1">ドリンク</div>
                  <h3 className="font-bold text-gray-800 text-sm mb-1 line-clamp-2">ホットコーヒー</h3>
                  <p className="text-orange-600 font-bold text-sm">¥320</p>
                </div>
              </div>
              
              <div className="flex-shrink-0 w-48 bg-white rounded-xl shadow-md overflow-hidden border border-gray-100" data-product-shop>
                <div className="aspect-square overflow-hidden bg-gray-50">
                  <img alt="アイスコーヒー" className="w-full h-full object-cover" src="https://saladfight.jp/cdn/shop/files/78dedaf3ec07867f8945399cbd4d268f.jpg?v=1748965588&width=533" />
                </div>
                <div className="p-3">
                  <div className="text-xs text-green-600 font-medium mb-1">ドリンク</div>
                  <h3 className="font-bold text-gray-800 text-sm mb-1 line-clamp-2">アイスコーヒー</h3>
                  <p className="text-orange-600 font-bold text-sm">¥320</p>
                </div>
              </div>
              
              <div className="flex-shrink-0 w-48 bg-white rounded-xl shadow-md overflow-hidden border border-gray-100" data-product-shop>
                <div className="aspect-square overflow-hidden bg-gray-50">
                  <img alt="サンペレグリノ" className="w-full h-full object-cover" src="https://saladfight.jp/cdn/shop/files/640dd1f95e02ab9319dd54f05d462050.png?v=1757344241&width=533" />
                </div>
                <div className="p-3">
                  <div className="text-xs text-green-600 font-medium mb-1">ドリンク</div>
                  <h3 className="font-bold text-gray-800 text-sm mb-1 line-clamp-2">サンペレグリノ</h3>
                  <p className="text-orange-600 font-bold text-sm">¥280</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">※プラン会員なら対象商品が¥0に</p>
          </div>
        </div>
      </section>

      {/* How to Use Ticket System Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-800 mb-4 sm:mb-6">
            チケット制の使い方
          </h2>
          <p className="text-center text-gray-600 mb-12 sm:mb-16 text-base sm:text-lg">
            シンプル3ステップで、社員の皆様が簡単にご利用いただけます
          </p>
          
          <div className="grid gap-8 sm:gap-12 md:grid-cols-3">
            <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-lg relative">
              <div className="absolute -top-4 -left-4 w-12 h-12 bg-green-500 text-white rounded-full flex items-center justify-center font-bold text-xl shadow-lg">
                1
              </div>
              <div className="mb-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=Business%20professional%20receiving%20paper%20meal%20tickets%20from%20HR%20department%20in%20modern%20Japanese%20office%2C%20friendly%20corporate%20atmosphere%2C%20employee%20receiving%20colorful%20food%20vouchers%2C%20professional%20business%20setting%2C%20natural%20office%20lighting%2C%20clean%20minimalist%20background&width=400&height=300&seq=ticket-receive&orientation=landscape"
                  alt="チケット受け取り"
                  className="w-full h-48 object-cover object-top rounded-xl"
                />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4">チケットを受け取る</h3>
              <p className="text-sm sm:text-base text-gray-600">
                会社の人事・総務部門から、月初めに紙チケットを受け取ります。チケットには有効期限が記載されています。
              </p>
            </div>
            
            <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-lg relative">
              <div className="absolute -top-4 -left-4 w-12 h-12 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold text-xl shadow-lg">
                2
              </div>
              <div className="mb-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=Japanese%20office%20worker%20walking%20to%20modern%20salad%20restaurant%20during%20lunch%20break%2C%20business%20person%20holding%20meal%20ticket%2C%20urban%20Osaka%20business%20district%2C%20professional%20attire%2C%20bright%20daylight%2C%20contemporary%20Japanese%20office%20environment&width=400&height=300&seq=ticket-visit&orientation=landscape"
                  alt="店舗へ来店"
                  className="w-full h-48 object-cover object-top rounded-xl"
                />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4">店舗へ来店</h3>
              <p className="text-sm sm:text-base text-gray-600">
                ランチタイムに肥後橋直結の店舗へ。チケットを持参するだけで、現金やカードは不要です。
              </p>
            </div>
            
            <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-lg relative">
              <div className="absolute -top-4 -left-4 w-12 h-12 bg-green-500 text-white rounded-full flex items-center justify-center font-bold text-xl shadow-lg">
                3
              </div>
              <div className="mb-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=Japanese%20office%20worker%20exchanging%20meal%20ticket%20for%20fresh%20healthy%20salad%20bowl%20at%20modern%20restaurant%20counter%2C%20friendly%20staff%20serving%20colorful%20nutritious%20salad%2C%20professional%20service%20interaction%2C%20bright%20clean%20restaurant%20interior%2C%20appetizing%20fresh%20vegetables&width=400&height=300&seq=ticket-exchange&orientation=landscape"
                  alt="サラダと交換"
                  className="w-full h-48 object-cover object-top rounded-xl"
                />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4">サラダと交換</h3>
              <p className="text-sm sm:text-base text-gray-600">
                チケットをスタッフに渡すだけで、お好きなサラダと交換完了。すぐにお召し上がりいただけます。
              </p>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-green-50 to-orange-50 p-6 sm:p-8 rounded-2xl">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
                <i className="ri-lightbulb-line text-3xl text-orange-500"></i>
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2">管理も簡単</h3>
                <p className="text-sm sm:text-base text-gray-600">
                  使用済みチケットは店舗で回収し、月末に利用枚数をご報告。請求書は翌月初めに発行いたします。複雑なシステム管理や個別精算は一切不要です。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Research Data Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-green-50 to-orange-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center text-gray-800 mb-4 sm:mb-6">
            健康ランチで、社員のパフォーマンスが最大20％アップ
          </h2>
          <p className="text-center text-gray-600 mb-12 sm:mb-16 text-base sm:text-lg">
            国際的な研究データが証明する、野菜・果物摂取の効果
          </p>
          
          <div className="grid gap-6 sm:gap-8 md:grid-cols-3">
            <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-lg text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <i className="ri-brain-line text-2xl text-blue-600"></i>
              </div>
              <div className="text-2xl sm:text-3xl font-bold text-blue-600 mb-2">+15%</div>
              <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-3 sm:mb-4">集中力・記憶力</h3>
              <p className="text-xs sm:text-sm text-gray-600">
                野菜を多く摂る人は認知機能が平均5歳若く見える
              </p>
            </div>
            
            <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-lg text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <i className="ri-arrow-up-line text-2xl text-green-600"></i>
              </div>
              <div className="text-2xl sm:text-3xl font-bold text-green-600 mb-2">+20%</div>
              <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-3 sm:mb-4">生産性</h3>
              <p className="text-xs sm:text-sm text-gray-600">
                職場で果物・野菜を取り入れると生産性が最大20％向上
              </p>
            </div>
            
            <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-lg text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <i className="ri-arrow-down-line text-2xl text-orange-600"></i>
              </div>
              <div className="text-2xl sm:text-3xl font-bold text-orange-600 mb-2">-15%</div>
              <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-3 sm:mb-4">エラー率</h3>
              <p className="text-xs sm:text-sm text-gray-600">
                午後の食後パフォーマンス低下を防ぎ、エラー率約15％減少
              </p>
            </div>
          </div>
          
          <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-lg">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6 text-center">研究データ根拠</h3>
            <div className="grid gap-4 sm:gap-6 md:grid-cols-2">
              <div className="flex items-start space-x-3 sm:space-x-4">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-sm sm:text-base text-gray-600">
                  <strong>米国神経学会研究：</strong>野菜を多く摂る人は認知機能が平均5歳若く見える
                </p>
              </div>
              <div className="flex items-start space-x-3 sm:space-x-4">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-sm sm:text-base text-gray-600">
                  <strong>ニュージーランド大学調査：</strong>果物・野菜の摂取が多い日は幸福度・活力・創造性が10〜15％上昇
                </p>
              </div>
              <div className="flex items-start space-x-3 sm:space-x-4">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-sm sm:text-base text-gray-600">
                  <strong>英ウォーリック大学調査：</strong>職場で果物・野菜を取り入れると生産性が最大20％向上
                </p>
              </div>
              <div className="flex items-start space-x-3 sm:space-x-4">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-sm sm:text-base text-gray-600">
                  <strong>パフォーマンス研究：</strong>午後の食後パフォーマンス低下を防ぎ、エラー率は約15％減少
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-800 mb-12 sm:mb-16">
            法人プラン料金
          </h2>
          
          <div className="grid gap-6 sm:gap-8 md:grid-cols-3">
            <div className="bg-gray-50 p-6 sm:p-8 rounded-2xl shadow-lg">
              <div className="text-center mb-4 sm:mb-6">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">トライアル</h3>
                <p className="text-sm sm:text-base text-gray-600">50枚〜 / 3ヶ月</p>
              </div>
              <div className="text-center mb-4 sm:mb-6">
                <div className="text-2xl sm:text-3xl font-bold text-orange-600">月額約4万5,000円〜</div>
                <p className="text-xs sm:text-sm text-gray-600">1食890円</p>
              </div>
              <ul className="space-y-2 sm:space-y-3 text-xs sm:text-sm text-gray-600">
                <li className="flex items-center">
                  <i className="ri-check-line text-green-500 mr-2"></i>
                  3ヶ月契約
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-green-500 mr-2"></i>
                  月50枚から利用可能
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-green-500 mr-2"></i>
                  導入サポート付き
                </li>
              </ul>
            </div>
            
            <div className="bg-green-50 p-6 sm:p-8 rounded-2xl shadow-lg border-2 border-green-500 relative">
              <div className="absolute -top-3 sm:-top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-green-500 text-white px-3 sm:px-4 py-1 sm:py-2 rounded-full text-xs sm:text-sm font-bold">
                  おすすめ
                </span>
              </div>
              <div className="text-center mb-4 sm:mb-6">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">スタンダード</h3>
                <p className="text-sm sm:text-base text-gray-600">300枚〜 / 6ヶ月</p>
              </div>
              <div className="text-center mb-4 sm:mb-6">
                <div className="text-2xl sm:text-3xl font-bold text-green-600">月額約26万4,000円〜</div>
                <p className="text-xs sm:text-sm text-gray-600">1食880円</p>
              </div>
              <ul className="space-y-2 sm:space-y-3 text-xs sm:text-sm text-gray-600">
                <li className="flex items-center">
                  <i className="ri-check-line text-green-500 mr-2"></i>
                  6ヶ月契約
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-green-500 mr-2"></i>
                  月300枚から利用可能
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-green-500 mr-2"></i>
                  専任サポート付き
                </li>
              </ul>
            </div>
            
            <div className="bg-orange-50 p-6 sm:p-8 rounded-2xl shadow-lg">
              <div className="text-center mb-4 sm:mb-6">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">プレミアム</h3>
                <p className="text-sm sm:text-base text-gray-600">1000枚〜 / 6ヶ月</p>
              </div>
              <div className="text-center mb-4 sm:mb-6">
                <div className="text-2xl sm:text-3xl font-bold text-orange-600">月額約145万円〜</div>
                <p className="text-xs sm:text-sm text-gray-600">1食870円</p>
              </div>
              <ul className="space-y-2 sm:space-y-3 text-xs sm:text-sm text-gray-600">
                <li className="flex items-center">
                  <i className="ri-check-line text-green-500 mr-2"></i>
                  6ヶ月契約
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-green-500 mr-2"></i>
                  月1000枚から利用可能
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-green-500 mr-2"></i>
                  優先サポート・カスタマイズ対応
                </li>
              </ul>
            </div>
          </div>
          
          <p className="text-center text-gray-600 text-base sm:text-lg px-4">
            <i className="ri-information-line mr-2"></i>
            社員1人あたり月2〜3万円のランチ代軽減＋健康投資
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-800 mb-12 sm:mb-16">
            導入メリット
          </h2>
          
          <div className="grid gap-6 sm:gap-8 md:grid-cols-3">
            <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-lg text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <i className="ri-user-add-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4">採用力UP</h3>
              <p className="text-sm sm:text-base text-gray-600">
                採用説明会で好印象を与え、他社との差別化を実現。優秀な人材の獲得につながります。
              </p>
            </div>
            
            <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-lg text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <i className="ri-shield-user-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4">若手定着率アップ</h3>
              <p className="text-sm sm:text-base text-gray-600">
                魅力的な福利厚生により離職率を下げ、採用・教育コストの削減を実現します。
              </p>
            </div>
            
            <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-lg text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <i className="ri-file-list-line text-2xl text-orange-600"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4">経理処理も簡単</h3>
              <p className="text-sm sm:text-base text-gray-600">
                法人請求書対応で経理処理がスムーズ。福利厚生費として計上可能です。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Store Access Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-800 mb-12 sm:mb-16">
            アクセス良好な立地
          </h2>
          
          <div className="grid gap-8 sm:gap-12 md:grid-cols-2 items-center">
            <div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6">肥後橋直結で通いやすい</h3>
              <div className="space-y-3 sm:space-y-4 text-sm sm:text-base text-gray-600">
                <div className="flex items-center">
                  <i className="ri-map-pin-line text-green-500 mr-3 flex-shrink-0"></i>
                  <span>地下鉄四つ橋線・京阪中之島線「肥後橋駅」直結</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-time-line text-green-500 mr-3 flex-shrink-0"></i>
                  <span>徒歩1分以内のアクセス</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-building-line text-green-500 mr-3 flex-shrink-0"></i>
                  <span>オフィス街の中心部で利便性抜群</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-shield-check-line text-green-500 mr-3 flex-shrink-0"></i>
                  <span>雨の日でも濡れずに来店可能</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Modern%20salad%20restaurant%20interior%20in%20Osaka%20business%20district%2C%20bright%20clean%20space%20with%20wooden%20tables%2C%20green%20plants%2C%20large%20windows%2C%20contemporary%20design%2C%20professional%20atmosphere%2C%20Japanese%20office%20workers%20dining%2C%20natural%20lighting%2C%20welcoming%20environment%20for%20corporate%20lunch&width=600&height=400&seq=store-interior&orientation=landscape"
                alt="店舗内観"
                className="w-full h-60 sm:h-80 object-cover object-top rounded-2xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-green-50 to-orange-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-800 mb-12 sm:mb-16">
            導入企業様の声
          </h2>
          
          <div className="grid gap-6 sm:gap-8 md:grid-cols-2">
            <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-lg">
              <div className="flex items-center mb-4 sm:mb-6">
                <div className="w-10 sm:w-12 h-10 sm:h-12 bg-green-100 rounded-full flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0">
                  <i className="ri-building-line text-green-600"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-800 text-sm sm:text-base">IT企業A社 人事部長</h4>
                  <p className="text-xs sm:text-sm text-gray-600">従業員数：80名</p>
                </div>
              </div>
              <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4">
                「導入前は近隣の飲食店が混雑してお昼難民状態でした。サラダランチ導入後、社員アンケートで満足度80％を達成。健康意識も向上し、午後の集中力が明らかに改善されました。」
              </p>
              <div className="flex text-yellow-400">
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
              </div>
            </div>
            
            <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-lg">
              <div className="flex items-center mb-4 sm:mb-6">
                <div className="w-10 sm:w-12 h-10 sm:h-12 bg-orange-100 rounded-full flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0">
                  <i className="ri-building-line text-orange-600"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-800 text-sm sm:text-base">商社B社 総務担当</h4>
                  <p className="text-xs sm:text-sm text-gray-600">従業員数：120名</p>
                </div>
              </div>
              <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4">
                「福利厚生の目玉として採用活動で大きな武器になっています。採用母集団が拡大し、特に健康志向の高い優秀な若手人材からの応募が増加しました。投資対効果は十分です。」
              </p>
              <div className="flex text-yellow-400">
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-800 mb-12 sm:mb-16">
            よくあるご質問
          </h2>
          
          <div className="space-y-4 sm:space-y-6">
            <div className="bg-gray-50 p-4 sm:p-6 rounded-2xl">
              <h3 className="font-bold text-gray-800 mb-2 sm:mb-3 flex items-start">
                <i className="ri-question-line text-green-500 mr-2 mt-0.5 flex-shrink-0"></i>
                <span className="text-sm sm:text-base">契約期間はどのくらいですか？</span>
              </h3>
              <p className="text-sm sm:text-base text-gray-600 ml-6">
                最短3ヶ月からご契約いただけます。スタンダード・プレミアムプランは6ヶ月契約となります。
              </p>
            </div>
            
            <div className="bg-gray-50 p-4 sm:p-6 rounded-2xl">
              <h3 className="font-bold text-gray-800 mb-2 sm:mb-3 flex items-start">
                <i className="ri-question-line text-green-500 mr-2 mt-0.5 flex-shrink-0"></i>
                <span className="text-sm sm:text-base">チケットの使い方を教えてください</span>
              </h3>
              <p className="text-sm sm:text-base text-gray-600 ml-6">
                社員の方がチケットを店舗に持参し、サラダと交換するシンプルなシステムです。複雑な操作は一切不要です。
              </p>
            </div>
            
            <div className="bg-gray-50 p-4 sm:p-6 rounded-2xl">
              <h3 className="font-bold text-gray-800 mb-2 sm:mb-3 flex items-start">
                <i className="ri-question-line text-green-500 mr-2 mt-0.5 flex-shrink-0"></i>
                <span className="text-sm sm:text-base">経理処理はどうなりますか？</span>
              </h3>
              <p className="text-sm sm:text-base text-gray-600 ml-6">
                法人請求書での対応が可能です。福利厚生費としての計上が可能で、税務上のメリットもございます。
              </p>
            </div>
            
            <div className="bg-gray-50 p-4 sm:p-6 rounded-2xl">
              <h3 className="font-bold text-gray-800 mb-2 sm:mb-3 flex items-start">
                <i className="ri-question-line text-green-500 mr-2 mt-0.5 flex-shrink-0"></i>
                <span className="text-sm sm:text-base">途中で枚数変更は可能ですか？</span>
              </h3>
              <p className="text-sm sm:text-base text-gray-600 ml-6">
                契約期間中でも利用状況に応じて枚数調整が可能です。詳細は担当者にご相談ください。
              </p>
            </div>
            
            <div className="bg-gray-50 p-4 sm:p-6 rounded-2xl">
              <h3 className="font-bold text-gray-800 mb-2 sm:mb-3 flex items-start">
                <i className="ri-question-line text-green-500 mr-2 mt-0.5 flex-shrink-0"></i>
                <span className="text-sm sm:text-base">導入までの期間はどのくらいですか？</span>
              </h3>
              <p className="text-sm sm:text-base text-gray-600 ml-6">
                ご契約後、最短1週間で導入可能です。事前説明会や社員向け案内もサポートいたします。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid gap-6 sm:gap-8 md:grid-cols-4">
            <div>
              <img 
                src="https://static.readdy.ai/image/6d1552f340f8b16d5538e9b5dc05368f/307cb145ec2a1832180b8031a904da90.png"
                alt="Salad Fight Logo"
                className="h-8 sm:h-10 mb-3 sm:mb-4 brightness-0 invert"
              />
              <p className="text-gray-400 text-xs sm:text-sm">
                健康的なサラダランチで<br />
                社員の健康と会社の成長を支援
              </p>
            </div>
            
            <div>
              <h4 className="font-bold mb-3 sm:mb-4 text-sm sm:text-base">サービス</h4>
              <ul className="space-y-1 sm:space-y-2 text-xs sm:text-sm text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">法人プラン</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">メニュー</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">店舗情報</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold mb-3 sm:mb-4 text-sm sm:text-base">サポート</h4>
              <ul className="space-y-1 sm:space-y-2 text-xs sm:text-sm text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">よくある質問</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">お問い合わせ</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">導入支援</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold mb-3 sm:mb-4 text-sm sm:text-base">お問い合わせ</h4>
              <ul className="space-y-1 sm:space-y-2 text-xs sm:text-sm text-gray-400">
                <li className="flex items-center">
                  <i className="ri-phone-line mr-2 flex-shrink-0"></i>
                  <span>06-6201-3000</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-mail-line mr-2 flex-shrink-0"></i>
                  <span>salad_pass@saladfight.jp</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-time-line mr-2 flex-shrink-0"></i>
                  <span>平日 10:00-17:00</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-700 mt-8 sm:mt-12 pt-6 sm:pt-8 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-xs sm:text-sm text-gray-400 text-center md:text-left">
              © 2024 Salad Corporate Service. All rights reserved.
            </p>
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
              <a href="#" className="text-gray-400 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">プライバシーポリシー</a>
              <a href="#" className="text-gray-400 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">利用規約</a>
              <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">Made with Readdy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
